
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import * as LucideIcons from "lucide-react";
import { FC } from "react";
import { LucideProps } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { PageSection, PageFeature } from "@/types/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import LanguageSelector from "@/components/LanguageSelector";
import { Skeleton } from "@/components/ui/skeleton";

// Filter out non-component exports from LucideIcons
const getIconComponents = () => {
  return Object.entries(LucideIcons).reduce((acc, [name, component]) => {
    if (typeof component === 'function' && name !== 'createLucideIcon') {
      acc[name] = component as FC<LucideProps>;
    }
    return acc;
  }, {} as Record<string, FC<LucideProps>>);
};

const AccessPage = () => {
  const { language, t } = useLanguage();

  const { data: sections, isLoading: sectionsLoading } = useQuery({
    queryKey: ['page-sections', 'acces'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_sections')
        .select('*')
        .eq('page', 'acces')
        .order('sort_order');
      
      if (error) throw error;
      return data as PageSection[];
    }
  });

  const { data: features, isLoading: featuresLoading } = useQuery({
    queryKey: ['page-features', 'acces'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_features')
        .select('*')
        .eq('page', 'acces')
        .order('sort_order');
      
      if (error) throw error;
      return data as PageFeature[];
    }
  });

  // Get icon components
  const iconComponents = getIconComponents();

  // Translate section content
  const sectionsToTranslate = sections?.reduce((acc, section) => {
    acc[`title_${section.id}`] = section.title;
    acc[`content_${section.id}`] = section.content;
    return acc;
  }, {} as Record<string, string>) || {};

  const { translatedContent: translatedSections, isLoading: translationLoading } = 
    useTranslatedContent(sectionsToTranslate);

  // Translate feature content
  const featuresToTranslate = features?.reduce((acc, feature) => {
    acc[`title_${feature.id}`] = feature.title;
    acc[`description_${feature.id}`] = feature.description || "";
    return acc;
  }, {} as Record<string, string>) || {};

  const { translatedContent: translatedFeatures, isLoading: featuresTranslationLoading } = 
    useTranslatedContent(featuresToTranslate);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar rightElement={<LanguageSelector />} />
      
      <main className="flex-1 container mx-auto py-8 px-4 mt-16">
        {sectionsLoading ? (
          <div className="mb-8">
            <Skeleton className="h-10 w-1/2 mb-4" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          sections?.map((section) => (
            <div key={section.id} className="mb-8">
              <h1 className="text-3xl font-playfair text-villa-blue mb-4">
                {translationLoading 
                  ? <Skeleton className="h-8 w-1/3" /> 
                  : translatedSections[`title_${section.id}`] || section.title}
              </h1>
              <p className="text-gray-600 mb-8 max-w-3xl">
                {translationLoading 
                  ? <Skeleton className="h-16 w-full" /> 
                  : translatedSections[`content_${section.id}`] || section.content}
              </p>
            </div>
          ))
        )}

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {featuresLoading ? (
            [...Array(6)].map((_, i) => (
              <Card key={`loading-${i}`}>
                <CardHeader>
                  <Skeleton className="h-8 w-3/4 mb-2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ))
          ) : (
            features?.map((feature) => {
              const Icon = feature.icon ? iconComponents[feature.icon] : null;
              return (
                <Card key={feature.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {Icon && <Icon className="h-5 w-5 text-villa-turquoise" />}
                      {featuresTranslationLoading 
                        ? <Skeleton className="h-6 w-32" /> 
                        : translatedFeatures[`title_${feature.id}`] || feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {(featuresTranslationLoading 
                        ? feature.description 
                        : translatedFeatures[`description_${feature.id}`] || feature.description
                      ).split('. ').map((line, index) => (
                        <li key={index}>{line}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AccessPage;
